"use client"

import { useState, useEffect, useRef } from "react"
import { Sparkles, MessageSquare, Brain, X, Send, Bot, User } from "lucide-react"

interface AIChatBoxProps {
  onSendMessage?: (message: string) => void
}

interface ChatMessage {
  id: string
  type: "user" | "ai"
  content: string
  timestamp: Date
}

export function AIChatBox({ onSendMessage }: AIChatBoxProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [message, setMessage] = useState("")
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "1",
      type: "ai",
      content: "Hello! I'm your AI Companion. How can I help you learn today?",
      timestamp: new Date(Date.now() - 300000), // 5 minutes ago
    },
  ])
  const [isLoading, setIsLoading] = useState(false)
  
  const chatInputRef = useRef<HTMLInputElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && chatInputRef.current) {
      setTimeout(() => {
        chatInputRef.current?.focus()
      }, 100)
    }
  }, [isExpanded])

  // Close chat when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        chatContainerRef.current &&
        !chatContainerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false)
      }
    }

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isExpanded])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isExpanded) {
        setIsExpanded(false)
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isExpanded])

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const handleToggleChat = () => {
    setIsExpanded(!isExpanded)
  }

  const handleCloseChat = () => {
    setIsExpanded(false)
  }

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    if (message.trim()) {
      // Add user message
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "user",
        content: message.trim(),
        timestamp: new Date(),
      }
      
      setMessages(prev => [...prev, userMessage])
      onSendMessage?.(message.trim())
      setMessage("")
      setIsLoading(true)

      // Simulate AI response after delay
      setTimeout(() => {
        const aiResponses = [
          "That's an interesting question! Let me help you understand this better. Remember that practice is key to mastering any skill.",
          "Great question! I'd be happy to explain. The key concept here is to break it down into smaller, manageable parts.",
          "I understand your question. Let me provide you with a clear explanation and some examples to help you grasp the concept.",
          "Thanks for asking! This is a common area where students need clarification. Here's what you need to know...",
          "Excellent question! This shows you're thinking critically. Let me guide you through the solution step by step."
        ]
        
        const randomResponse = aiResponses[Math.floor(Math.random() * aiResponses.length)]
        
        const aiMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          type: "ai",
          content: randomResponse,
          timestamp: new Date(),
        }
        
        setMessages(prev => [...prev, aiMessage])
        setIsLoading(false)
      }, 1500)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value)
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div
      ref={chatContainerRef}
      className="fixed bottom-6 right-6 z-50"
    >
      {/* Chat Container */}
      <div
        className={`
          bg-white border border-gray-200 rounded-lg shadow-xl
          transition-all duration-300 ease-in-out overflow-hidden
          ${isExpanded ? "w-80 h-[500px]" : "w-12 h-12"}
          flex flex-col
        `}
      >
        {/* Chat Header/Toggle Button */}
        <div className={`
          flex items-center justify-center flex-shrink-0
          transition-all duration-200
          ${isExpanded ? "h-12 bg-teal-600 px-4" : "h-12"}
          ${!isExpanded ? "cursor-pointer bg-blue-800/50 hover:bg-blue-700/50 hover:to-pink-600" : ""}
        `}>
          {isExpanded ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Bot className="w-5 h-5 text-white" />
                  <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-300" />
                </div>
                <span className="text-white font-semibold text-sm">AI Companion</span>
              </div>
              <button
                onClick={handleCloseChat}
                className="flex items-center justify-center w-6 h-6 text-white/80 hover:text-white transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleToggleChat}
              className="w-12 h-12 flex items-center justify-center"
              aria-label="Open AI chat"
            >
              <div className="relative">
                <Brain className="w-6 h-6 text-gray-50 group-hover:text-white transition-colors" />
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400" />
              </div>
            </button>
          )}
        </div>

        {/* Chat Messages Area */}
        {isExpanded && (
          <div className="flex-1 flex flex-col p-0">
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`
                      max-w-[80%] rounded-lg p-3
                      ${msg.type === "user" 
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-none" 
                        : "bg-white border border-gray-200 rounded-bl-none shadow-sm"
                      }
                    `}
                  >
                    <div className="flex items-start gap-2">
                      {msg.type === "ai" && (
                        <div className="flex-shrink-0 mt-0.5">
                          <div className="relative">
                            <Bot className="w-4 h-4 text-purple-600" />
                            <Sparkles className="absolute -top-1 -right-1 w-2 h-2 text-yellow-400" />
                          </div>
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm">{msg.content}</p>
                        <div className={`text-xs mt-1 ${msg.type === "user" ? "text-blue-100" : "text-gray-400"}`}>
                          {formatTime(msg.timestamp)}
                        </div>
                      </div>
                      {msg.type === "user" && (
                        <div className="flex-shrink-0 mt-0.5">
                          <User className="w-4 h-4 text-blue-200" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-lg rounded-bl-none p-3 shadow-sm max-w-[80%]">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Bot className="w-4 h-4 text-purple-600" />
                        <Sparkles className="absolute -top-1 -right-1 w-2 h-2 text-yellow-400" />
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                        <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                        <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input Area */}
            <div className="border-t border-gray-200 p-3 bg-white">
              <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={message}
                  onChange={handleInputChange}
                  placeholder="Ask your AI companion..."
                  className="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isLoading}
                  aria-label="Type your message"
                />
                <button
                  type="submit"
                  disabled={!message.trim() || isLoading}
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-lg
                    ${message.trim() && !isLoading
                      ? "bg-teal-600 text-white hover:opacity-90"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }
                    transition-all duration-200
                  `}
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              
              {/* Quick Suggestions */}
              <div className="mt-2 flex flex-wrap gap-1">
                {["Help with math", "Explain science", "Study tips", "Career advice"].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setMessage(suggestion)}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    type="button"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Badge (Optional) */}
      {!isExpanded && messages.length > 1 && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
          <span className="text-xs text-white font-bold">{messages.length - 1}</span>
        </div>
      )}
    </div>
  )
}